import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, CodeTag, Drawer, Inline, PageHeader, Segmented, Stack, t, Text, TextField } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../../components/Shell';
import { api, unwrap } from '../../lib/api';
import { relativeTime, shortDate } from '../../lib/format';
import { EDGES, FEATURES, LiveKey, NODES, NOW, PhaseId, PHASES, PLAN_DATE, PlanEdge, PlanFeature, PlanNode } from './plan';
import './roadmap.css';

type Live = maintenance.paths['/roadmap/live']['get']['responses']['200']['content']['application/json'];
type Status = 'live' | 'pending' | 'progress' | 'planned';
type Filter = Status | 'all';

const rank = (p: PhaseId) => PHASES.indexOf(p);
const edgeId = (e: PlanEdge) => `${e.from}__${e.to}`;
const nodeOf = (id: string) => NODES.find(n => n.id === id)!;

// Geometry of the four strata: écrans, services, moteurs, parc.
const COL_X = [24, 392, 760, 1128];
const COL_W = [244, 244, 244, 224];
const TOP = 76;
const ROW = 54;
const BOX_H = 40;
const byCol = [0, 1, 2, 3].map(c => NODES.filter(n => n.col === c));
const MAX_ROWS = Math.max(...byCol.map(c => c.length));
const W = 1368;
const H = TOP + MAX_ROWS * ROW + 12;
const pos = (n: PlanNode) => {
  const col = byCol[n.col]!;
  const offset = n.col === 3 ? Math.round((MAX_ROWS - col.length) / 2) * ROW : 0;
  return { x: COL_X[n.col]!, w: COL_W[n.col]!, y: TOP + offset + col.indexOf(n) * ROW };
};
function edgePath(e: PlanEdge) {
  const a = nodeOf(e.from), b = nodeOf(e.to), pa = pos(a), pb = pos(b);
  const ay = pa.y + BOX_H / 2, by = pb.y + BOX_H / 2;
  if (a.col === b.col) {
    const x = pa.x + pa.w, bulge = 26 + Math.min(60, Math.abs(by - ay) / 6);
    return `M${x},${ay} C${x + bulge},${ay} ${x + bulge},${by} ${x},${by}`;
  }
  const forward = b.col > a.col;
  const x1 = forward ? pa.x + pa.w : pa.x, x2 = forward ? pb.x : pb.x + pb.w;
  const dx = (x2 - x1) * 0.45;
  return `M${x1},${ay} C${x1 + dx},${ay} ${x2 - dx},${by} ${x2},${by}`;
}
function labelPoint(e: PlanEdge) {
  const a = nodeOf(e.from), b = nodeOf(e.to), pa = pos(a), pb = pos(b);
  const x = a.col === b.col ? pa.x + pa.w + 52 : a.col < b.col ? (pa.x + pa.w + pb.x) / 2 : (pb.x + pb.w + pa.x) / 2;
  return { x, y: (pa.y + pb.y) / 2 + BOX_H / 2 };
}

/** What the plan cannot know comes from the portal's state; until it answers, the plan's own view. */
function liveStatus(key: LiveKey, live: Live | undefined): Status {
  if (key === 'enrolment') return live && live.enrolment.servers > 0 && live.enrolment.enrolled >= live.enrolment.servers ? 'live' : 'progress';
  if (!live) return 'pending';
  const ok = key === 'github' ? live.github === 'connected' : live[key];
  return ok ? 'live' : 'pending';
}
const statusOf = (item: { phase: PhaseId; live?: LiveKey }, live: Live | undefined): Status =>
  rank(item.phase) > rank(NOW) ? 'planned' : item.live ? liveStatus(item.live, live) : 'live';

/** A sentence of the catalogue with `code` spans, as the DSM writes inline code. */
function Rich({ text }: { text: string }) {
  return <>{text.split(/`([^`]+)`/).map((part, i) => (i % 2 ? <CodeTag key={i}>{part}</CodeTag> : <Fragment key={i}>{part}</Fragment>))}</>;
}

/**
 * « Carte du projet » (SPEC-031): the system drawn as screens, portal services,
 * engines and fleet, linked by what they exchange; every feature of the plan
 * with its status, lit on the map when chosen. The plan comes from the roadmap,
 * what depends on the platform's state comes from GET /roadmap/live.
 */
export function RoadmapPage() {
  const [phase, setPhase] = useState<PhaseId>(NOW);
  const [feature, setFeature] = useState<string | null>(null);
  const [node, setNode] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const mapRef = useRef<HTMLElement>(null);
  const live = useQuery({
    queryKey: ['roadmap-live'],
    queryFn: async () => unwrap(await api.maintenance.GET('/roadmap/live')),
    refetchInterval: 30_000,
  }).data;

  useEffect(() => {
    // The Drawer closes itself on Escape (DSM overlay stack); the map's selection clears only without it.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[role="dialog"]')) { setFeature(null); setNode(null); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const lit = useMemo(() => {
    if (feature) {
      const f = FEATURES.find(x => x.id === feature)!;
      return { nodes: new Set(f.nodes), edges: new Set(f.edges) };
    }
    if (node) {
      const edges = EDGES.filter(e => e.from === node || e.to === node);
      return { nodes: new Set([node, ...edges.flatMap(e => [e.from, e.to])]), edges: new Set(edges.map(edgeId)) };
    }
    return null;
  }, [feature, node]);

  const future = (item: { phase: PhaseId }) => rank(item.phase) > rank(phase);
  const featureStatus = (f: PlanFeature) => statusOf(f, live);
  const counts = { all: FEATURES.length } as Record<Filter, number>;
  for (const f of FEATURES) counts[featureStatus(f)] = (counts[featureStatus(f)] ?? 0) + 1;

  const q = query.trim().toLowerCase();
  const related = node ? new Set(FEATURES.filter(f => f.nodes.includes(node)).map(f => f.id)) : null;
  const groups = PHASES.map(p => ({
    phase: p,
    items: FEATURES.filter(f => f.phase === p)
      .filter(f => filter === 'all' || featureStatus(f) === filter)
      .filter(f => !q || `${t(`roadmap.features.${f.id}.title`)} ${t(`roadmap.features.${f.id}.desc`)} ${f.nodes.map(id => t(`roadmap.nodes.${id}.name`)).join(' ')}`.toLowerCase().includes(q)),
  })).filter(g => g.items.length);

  const chooseFeature = (id: string) => {
    setFeature(f => (f === id ? null : id));
    setNode(null);
    // The list sits under the map: bring the map back into view to see the feature lit.
    const top = mapRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0) mapRef.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  };
  const chooseNode = (id: string) => { setNode(n => (n === id ? null : id)); setFeature(null); };
  const clear = () => { setFeature(null); setNode(null); };
  const statusBadge = (s: Status) => <Badge tone={s === 'live' ? 'pos' : s === 'pending' ? 'warn' : s === 'progress' ? 'info' : 'neutral'}>{t(`roadmap.status.${s}`)}</Badge>;

  return (
    <Shell crumbs={[{ label: t('roadmap.title') }]}>
      <Stack gap={16}>
        <PageHeader
          title={t('roadmap.title')}
          subtitle={t('roadmap.subtitle')}
          actions={<Text variant="body-sm" tone="mute">{t('roadmap.stamp', { date: shortDate(PLAN_DATE), time: live ? relativeTime(live.checkedAt) : '…' })}</Text>}
        />
        <div className="rm-summary">
          {(['live', 'pending', 'progress', 'planned'] as const).map(s => (
            <span key={s} className="rm-pill"><span className={`rm-dot ${s}`} />{t(`roadmap.summary.${s}`, { n: counts[s] ?? 0 })}</span>
          ))}
          {live && <span className="rm-pill">{t('roadmap.summary.enrolled', { n: live.enrolment.enrolled, enrolled: live.enrolment.enrolled, servers: live.enrolment.servers })}</span>}
        </div>
        <div className="rm-timeline">
          <span className="rm-timeline-label" id="rm-timeline">{t('roadmap.timeline')}</span>
          <Segmented
            label={t('roadmap.timeline')}
            value={phase}
            onChange={id => setPhase(id as PhaseId)}
            items={PHASES.map(p => ({ id: p, label: p === NOW ? t('roadmap.today', { phase: t(`roadmap.phases.${p}.label`) }) : t(`roadmap.phases.${p}.label`) }))}
          />
          <span className="rm-hint">{t('roadmap.timelineHint', { n: FEATURES.filter(f => rank(f.phase) <= rank(phase)).length, total: FEATURES.length })}</span>
        </div>

        <div className="rm-main">

          <section className="rm-map" aria-label={t('roadmap.map')} ref={mapRef}>
            <div className="rm-scroll">
              <MapSvg phase={phase} live={live} lit={lit} future={future} onNode={chooseNode} onInfo={setInfo} />
            </div>
            <div className="rm-detail">
              <Detail feature={feature} node={node} phase={phase} live={live} onClear={clear} statusBadge={statusBadge} />
            </div>
            <div className="rm-legend">
              {(['live', 'pending', 'progress', 'planned'] as const).map(s => <span key={s}><span className={`rm-dot ${s}`} />{t(`roadmap.legend.${s}`)}</span>)}
              <span><span className="rm-line" />{t('roadmap.legend.edge')}</span>
              <span><span className="rm-line future" />{t('roadmap.legend.future')}</span>
              <span><span className="rm-line on" />{t('roadmap.legend.lit')}</span>
            </div>
          </section>

          <aside className="rm-index" aria-label={t('roadmap.list')}>
            <div className="rm-index-head">
              <TextField type="search" ariaLabel={t('roadmap.search')} placeholder={t('roadmap.search')} value={query} onChange={(e: { target: { value: string } }) => setQuery(e.target.value)} size="sm" />
              <div className="rm-chips" role="group" aria-label={t('roadmap.filter')}>
                {(['all', 'live', 'pending', 'progress', 'planned'] as const).map(k => (
                  <button key={k} type="button" className="rm-chip" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                    {k !== 'all' && <span className={`rm-dot ${k}`} />}{t(`roadmap.filters.${k}`)} <b>{counts[k] ?? 0}</b>
                  </button>
                ))}
              </div>
            </div>
            <div className="rm-list">
              {groups.length === 0 && <p className="rm-empty">{t('roadmap.empty')}</p>}
              {groups.map(g => (
                <div key={g.phase}>
                  <h3 className="rm-group-title"><span>{t(`roadmap.phases.${g.phase}.long`)}</span><span>{g.items.length}</span></h3>
                  {g.items.map(f => {
                    const s = featureStatus(f);
                    return (
                      <button key={f.id} type="button" className={`rm-feat${related?.has(f.id) ? ' related' : ''}`} aria-pressed={feature === f.id} onClick={() => chooseFeature(f.id)}>
                        <span className={`rm-dot ${s}`} />
                        <span className="rm-feat-title">{t(`roadmap.features.${f.id}.title`)}</span>
                        <span className="rm-feat-status">{t(`roadmap.status.${s}`)}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </Stack>
      <NodeDrawer id={info} live={live} future={future} statusBadge={statusBadge}
        onClose={() => setInfo(null)}
        onShow={id => { setInfo(null); setFeature(null); setNode(id); }}
        onFeature={id => { setInfo(null); chooseFeature(id); }} />
    </Shell>
  );
}

function MapSvg({ phase, live, lit, future, onNode, onInfo }: {
  phase: PhaseId;
  live: Live | undefined;
  lit: { nodes: Set<string>; edges: Set<string> } | null;
  future: (item: { phase: PhaseId }) => boolean;
  onNode: (id: string) => void;
  onInfo: (id: string) => void;
}) {
  return (
    <svg className="rm-svg" viewBox={`0 0 ${W} ${H}`} role="group" aria-label={t('roadmap.map')} data-phase={phase}>
      {[0, 1, 2, 3].map(i => (
        <g key={i}>
          <rect className="rm-band" x={COL_X[i]! - 10} y={8} width={COL_W[i]! + 20} height={H - 12} rx={12} />
          <text className="rm-strata-title" x={COL_X[i]} y={34}>{t(`roadmap.strata.${i}.title`)}</text>
          <text className="rm-strata-sub" x={COL_X[i]} y={52}>{t(`roadmap.strata.${i}.sub`)}</text>
        </g>
      ))}
      <g>
        {EDGES.map(e => {
          const id = edgeId(e);
          const cls = ['rm-edge'];
          if (future(e)) cls.push('future');
          else if (e.live && liveStatus(e.live, live) !== 'live') cls.push('pending');
          if (lit) cls.push(lit.edges.has(id) ? 'on' : 'dim');
          return <path key={id} className={cls.join(' ')} d={edgePath(e)}><title>{`${t(`roadmap.nodes.${e.from}.name`)} → ${t(`roadmap.nodes.${e.to}.name`)} : ${t(`roadmap.edges.${id}`)}`}</title></path>;
        })}
      </g>
      <g>
        {NODES.map(n => {
          const p = pos(n);
          const s = statusOf(n, live);
          const isFuture = future(n);
          const cls = ['rm-node', isFuture ? 'future' : s];
          if (lit) cls.push(lit.nodes.has(n.id) ? 'on' : 'dim');
          const name = t(`roadmap.nodes.${n.id}.name`);
          return (
            <g key={n.id} className={cls.join(' ')} tabIndex={0} role="button"
              onClick={() => onNode(n.id)}
              onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onNode(n.id); } }}>
              {/* Its accessible name: everything it shows, then its status. */}
              <title>{t('roadmap.nodeAria', { name, sub: t(`roadmap.nodes.${n.id}.sub`), phase: t(`roadmap.phases.${n.phase}.label`), status: t(`roadmap.status.${isFuture ? 'planned' : s}`) })}</title>
              <rect className="box" x={p.x} y={p.y} width={p.w} height={BOX_H} rx={8} />
              <circle className={`rm-dot-${isFuture ? 'planned' : s}`} cx={p.x + 13} cy={p.y + 14} r={4.5} strokeWidth={1.6} />
              <text className="name" x={p.x + 24} y={p.y + 17}>{name}</text>
              <text className="sub" x={p.x + 24} y={p.y + 32}>{t(`roadmap.nodes.${n.id}.sub`)}</text>
              <text className="tag" x={p.x + p.w - 8} y={p.y + 17} textAnchor="end">{t(`roadmap.phases.${n.phase}.label`)}</text>
            </g>
          );
        })}
      </g>
      <g>
        {/* « i »: what the block is, in a Drawer. A sibling of the node, never nested in its button. */}
        {NODES.map(n => {
          const p = pos(n);
          const name = t(`roadmap.nodes.${n.id}.name`);
          return (
            <g key={n.id} className={`rm-info${lit && !lit.nodes.has(n.id) ? ' dim' : ''}`} tabIndex={0} role="button"
              onClick={() => onInfo(n.id)}
              onKeyDown={ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onInfo(n.id); } }}>
              <title>{t('roadmap.info', { name })}</title>
              <circle cx={p.x + p.w - 13} cy={p.y + BOX_H - 12} r={7.5} />
              <text x={p.x + p.w - 13} y={p.y + BOX_H - 8.5} textAnchor="middle">{t('roadmap.infoGlyph')}</text>
            </g>
          );
        })}
      </g>
      <g>{lit && EDGES.filter(e => lit.edges.has(edgeId(e))).map(e => <EdgeLabel key={edgeId(e)} edge={e} />)}</g>
    </svg>
  );
}

/** An exchange's label on its own background, sized to its text. */
function EdgeLabel({ edge }: { edge: PlanEdge }) {
  const ref = useRef<SVGTextElement>(null);
  const [box, setBox] = useState<DOMRect | null>(null);
  useLayoutEffect(() => { if (ref.current) setBox(ref.current.getBBox()); }, []);
  const p = labelPoint(edge);
  return (
    <g>
      {box && <rect className="rm-elabel-bg" x={box.x - 5} y={box.y - 2} width={box.width + 10} height={box.height + 4} rx={4} />}
      <text ref={ref} className="rm-elabel" x={p.x} y={p.y + 3.5} textAnchor="middle">{t(`roadmap.edges.${edgeId(edge)}`)}</text>
    </g>
  );
}

function Detail({ feature, node, phase, live, onClear, statusBadge }: {
  feature: string | null;
  node: string | null;
  phase: PhaseId;
  live: Live | undefined;
  onClear: () => void;
  statusBadge: (s: Status) => JSX.Element;
}) {
  const liveNote = (key: LiveKey | undefined) => {
    if (!key || !live) return null;
    if (key === 'enrolment') return t('roadmap.live.enrolment', { n: live.enrolment.enrolled, enrolled: live.enrolment.enrolled, servers: live.enrolment.servers });
    if (key === 'github' && live.github === 'failing') return t('roadmap.live.githubFailing');
    return null;
  };
  const actionAlert = (s: Status, text: string, note: string | null) => (s === 'pending' || s === 'progress') && (
    <Alert tone={s === 'progress' ? 'info' : 'warn'} title={s === 'progress' ? t('roadmap.where') : t('roadmap.todo')}>
      {note ? `${note} ` : ''}<Rich text={text} />
    </Alert>
  );
  if (feature) {
    const f = FEATURES.find(x => x.id === feature)!;
    const s = statusOf(f, live);
    return (
      <Stack gap={8}>
        <Inline gap={8} align="center" justify="between">
          <Text variant="card-title" as="h2">{t(`roadmap.features.${f.id}.title`)}</Text>
          <Button variant="secondary" size="sm" onClick={onClear}>{t('roadmap.clear')}</Button>
        </Inline>
        <Inline gap={8}>{statusBadge(s)}<Badge tone="neutral" variant="ring">{t(`roadmap.phases.${f.phase}.long`)}</Badge></Inline>
        <Text><Rich text={t(`roadmap.features.${f.id}.desc`)} /></Text>
        {f.action && actionAlert(s, t(`roadmap.features.${f.id}.action`), liveNote(f.live))}
        <Text variant="body-sm" tone="mute">{t('roadmap.uses')} : {f.nodes.map(id => t(`roadmap.nodes.${id}.name`)).join(' · ')}</Text>
      </Stack>
    );
  }
  if (node) {
    const n = nodeOf(node);
    const s = statusOf(n, live);
    const fs = FEATURES.filter(f => f.nodes.includes(n.id));
    const exchanges = EDGES.filter(e => e.from === n.id || e.to === n.id);
    return (
      <Stack gap={8}>
        <Inline gap={8} align="center" justify="between">
          <Text variant="card-title" as="h2">{t(`roadmap.nodes.${n.id}.name`)}</Text>
          <Button variant="secondary" size="sm" onClick={onClear}>{t('roadmap.clear')}</Button>
        </Inline>
        <Inline gap={8}>{statusBadge(s)}<Badge tone="neutral" variant="ring">{t(`roadmap.phases.${n.phase}.long`)}</Badge><Badge tone="neutral" variant="ring">{t(`roadmap.strata.${n.col}.title`)}</Badge></Inline>
        <Text>{t(`roadmap.nodes.${n.id}.sub`)}. {t('roadmap.nodeFeatures', { n: fs.length })}</Text>
        {n.action && actionAlert(s, t(`roadmap.nodes.${n.id}.action`), liveNote(n.live))}
        <Text variant="body-sm" tone="mute">
          {t('roadmap.exchanges')} : {exchanges.length ? exchanges.map(e => `${e.from === n.id ? '→ ' + t(`roadmap.nodes.${e.to}.name`) : '← ' + t(`roadmap.nodes.${e.from}.name`)} ${t(`roadmap.edges.${edgeId(e)}`)}`).join(' · ') : t('roadmap.none')}
        </Text>
      </Stack>
    );
  }
  const help = phase === NOW ? t('roadmap.phaseNow') : rank(phase) < rank(NOW) ? t('roadmap.phasePast') : t('roadmap.phaseFuture');
  return (
    <Stack gap={8}>
      <Text variant="card-title" as="h2">{t(`roadmap.phases.${phase}.long`)}</Text>
      <Text>{help} {t('roadmap.phaseHelp')}</Text>
    </Stack>
  );
}

/** What a building block is, what it exchanges with, which features use it. */
function NodeDrawer({ id, live, future, statusBadge, onClose, onShow, onFeature }: {
  id: string | null;
  live: Live | undefined;
  future: (item: { phase: PhaseId }) => boolean;
  statusBadge: (s: Status) => JSX.Element;
  onClose: () => void;
  onShow: (id: string) => void;
  onFeature: (id: string) => void;
}) {
  const n = id ? nodeOf(id) : null;
  const s = n ? (future(n) ? 'planned' : statusOf(n, live)) : 'planned';
  const exchanges = n ? EDGES.filter(e => e.from === n.id || e.to === n.id) : [];
  const fs = n ? FEATURES.filter(f => f.nodes.includes(n.id)) : [];
  return (
    <Drawer open={Boolean(n)} onClose={onClose} size="xl"
      eyebrow={t('roadmap.drawer.eyebrow')}
      title={n ? t(`roadmap.nodes.${n.id}.name`) : ''}
      subtitle={n ? t(`roadmap.nodes.${n.id}.sub`) : ''}
      footer={n && <Inline gap={8} justify="end"><Button variant="secondary" onClick={onClose}>{t('roadmap.drawer.close')}</Button><Button icon="hub" onClick={() => onShow(n.id)}>{t('roadmap.drawer.show')}</Button></Inline>}>
      {n && (
        <Stack gap={16}>
          <Inline gap={8}>{statusBadge(s)}<Badge tone="neutral" variant="ring">{t(`roadmap.phases.${n.phase}.long`)}</Badge><Badge tone="neutral" variant="ring">{t(`roadmap.strata.${n.col}.title`)}</Badge></Inline>
          <Stack gap={4}>
            <Text variant="card-title" as="h3">{t('roadmap.drawer.what')}</Text>
            <Text><Rich text={t(`roadmap.nodes.${n.id}.about`)} /></Text>
          </Stack>
          {n.action && (s === 'pending' || s === 'progress') && <Alert tone="warn" title={t('roadmap.todo')}><Rich text={t(`roadmap.nodes.${n.id}.action`)} /></Alert>}
          <Stack gap={4}>
            <Text variant="card-title" as="h3">{t('roadmap.drawer.exchanges')}</Text>
            {exchanges.length ? (
              <ul className="rm-drawer-list">
                {exchanges.map(e => (
                  <li key={edgeId(e)}>
                    <b>{e.from === n.id ? `→ ${t(`roadmap.nodes.${e.to}.name`)}` : `← ${t(`roadmap.nodes.${e.from}.name`)}`}</b>
                    <span>{t(`roadmap.edges.${edgeId(e)}`)}</span>
                  </li>
                ))}
              </ul>
            ) : <Text tone="mute">{t('roadmap.none')}</Text>}
          </Stack>
          <Stack gap={4}>
            <Text variant="card-title" as="h3">{t('roadmap.drawer.features')}</Text>
            {fs.length ? (
              <ul className="rm-drawer-list">
                {fs.map(f => (
                  <li key={f.id}>
                    <button type="button" className="rm-drawer-link" onClick={() => onFeature(f.id)}>{t(`roadmap.features.${f.id}.title`)}</button>
                    <span>{t(`roadmap.phases.${f.phase}.label`)} · {t(`roadmap.status.${statusOf(f, live)}`)}</span>
                  </li>
                ))}
              </ul>
            ) : <Text tone="mute">{t('roadmap.none')}</Text>}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
